import { useQuery } from '@tanstack/react-query'
import { Button, Select } from 'antd'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import ViewIcon from '@assets/icons/viewSvg'
import Header from '@components/header'
import Layout from '@components/Layout'
import DebtsOneSlpModal from '@components/Modal/DebtSalesPersonModal/DebtsOneSlp'
import ViewSalesModal from '@components/Modal/ViewSalesWithDocEntryModal'
import { columnDebtorsSalesPerson } from '@utils/columns'
import { debouncedSet } from '@utils/debounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchOrderProfit, fetchDepartments } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'
import { getToday } from '@utils/time'



const OrderProfit = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [slpName, setSlpName] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [department, setDepartment] = useState('')
	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)
	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'order-profit',
			pageSize,
			page,
			startDate,
			endDate,
			department,
		],
		queryFn: fetchOrderProfit,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	// Step 1: Collect ALL unique months from ALL records
	const allMonths = []

	data.forEach((record) => {
		record.months?.forEach((month) => {
			// Avoid duplicates by using monthIndex + monthYear as unique key
			if (
				!allMonths.find(
					(m) =>
						m.monthIndex === month.monthIndex &&
						m.monthYear === month.monthYear,
				)
			) {
				allMonths.push(month)
			}
		})
	})

	// Optional: Sort months from newest to oldest (October → June)
	allMonths.sort((a, b) => b.monthIndex - a.monthIndex)

	// Step 2: Base columns (fixed)
	const baseColumns = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (value) => moment(value).format('DD.MM.YYYY'),
		},
		{
			title: t('department'),
			dataIndex: 'departmentName',
			key: 'departmentName',
		},
		{
			title: t('sellingPrice'),
			dataIndex: 'sellingPrice',
			key: 'sellingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('purchasingPrice'),
			dataIndex: 'purchasingPrice',
			key: 'purchasingPrice',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('workerSalaries'),
			dataIndex: 'workerSalaries',
			key: 'workerSalaries',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('costs'),
			dataIndex: 'costs',
			key: 'costs',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('grossProfit'),
			dataIndex: 'grossProfit',
			key: 'grossProfit',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('action'),
			dataIndex: 'action',
			key: 'action',
			render: (value, record) => (
				<div className="flex flex-col md:flex-row gap-2 items-center justify-start">
					<button
						onClick={() => handleViewClick(record)}
						className="bg-[#2198c7] text-white w-[35px] h-[30px] rounded-lg flex justify-center items-center"
					>
						<ViewIcon />
					</button>
				</div>
			),
		},
	]

	const columns = [...baseColumns]

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prevPage) => prevPage - 1)
		}
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/salesdata/profit-by-orders-excel',
			params: {
				department,
				startDate,
				endDate,
				page: 0,
				pageSize: 1000,
			},
			fileName: 'Yalpi foyda hisobi',
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'Yalpi foyda hisobi'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				hasMoreData={data.length === pageSize}
				currentPage={page + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				onStartDateChange={handleStartDate}
				onEndDateChange={handleEndDate}
				startDate={startDate}
				endDate={endDate}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				renderRightHeader={() => {
					return (
						<div className="flex flex-col gap-2">
							<p className="font-nunito">{t('department')}</p>
							<Select
								value={department}
								className="flex h-8  w-[200px] items-center justify-center "
								style={{ color: 'black' }}
								onSelect={(val) => {
									setDepartment(val)
								}}
								options={departmentData.map((department) => {
									return {
										label: department.name,
										value: Number(department.code),
									}
								})}
								filterOption={(input, option) =>
									option.label
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								showSearch
								allowClear
								onClear={() => {
									setDepartment('')
								}}
							/>
						</div>
					)
				}}
			/>
			<ViewSalesModal
				visible={viewModalOpen}
				data={selectedRowData}
				onClose={() => setViewModalOpen(false)}
				isShowProfit={true}
			/>
		</Layout>
	)
}

export default OrderProfit
